import { motion } from "framer-motion";
import { Check, ShieldCheck, HelpCircle } from "lucide-react";
import { BOOKING_URL } from "@/constants/site";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { iconFor } from "@/lib/icons";
import { scrollToSection } from "@/lib/scroll";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const PackageCard = ({ pkg }) => {
  const Icon = iconFor(pkg.icon);
  return (
    <motion.a
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`package-card-${pkg.id}`}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      className={`group relative flex flex-col bg-[#0A0A0A] p-7 transition-[background-color] duration-500 hover:bg-[#101010] md:p-9 ${
        pkg.featured ? "md:col-span-7" : "md:col-span-5"
      }`}
    >
      {pkg.featured && (
        <span className="absolute right-0 top-0 bg-[#E60000] px-4 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.25em] text-white">
          Most complete
        </span>
      )}
      <div className="flex items-start justify-between">
        <span className="flex h-12 w-12 items-center justify-center border border-[#E60000]/60 text-[#E60000] transition-[background-color,color] duration-300 group-hover:bg-[#E60000] group-hover:text-white">
          <Icon size={22} strokeWidth={1.75} />
        </span>
        <span className="font-display text-sm font-medium tracking-[0.3em] text-zinc-600">
          {pkg.index}
        </span>
      </div>

      <h3 className="font-display mt-6 text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">
        {pkg.name}
      </h3>

      <div className="mt-3 flex items-baseline gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-500">
          Starting at
        </span>
        <span className="font-display text-4xl font-bold text-[#E60000] md:text-5xl">
          {pkg.price}
        </span>
      </div>

      <ul className="mt-6 grid flex-1 grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
        {pkg.includes.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-sm text-zinc-300">
            <Check size={15} strokeWidth={3} className="mt-0.5 shrink-0 text-[#E60000]" />
            {item}
          </li>
        ))}
      </ul>

      <span className="mt-8 inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 transition-[color] duration-300 group-hover:text-[#E60000]">
        Book this package
        <span className="h-px w-8 bg-current transition-[width] duration-300 group-hover:w-14" />
      </span>
    </motion.a>
  );
};

export const Services = () => {
  const { config } = useSiteConfig();
  return (
  <section id="services" data-testid="services-section" className="relative py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-5 md:px-10">
      <div className="mb-14 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
        <div>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]"
          >
            <span className="h-px w-10 bg-[#E60000]" />
            The menu
          </motion.p>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl"
          >
            Pick your <span className="text-outline-red">detail</span>
          </motion.h2>
        </div>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="max-w-sm"
        >
          <p className="text-sm leading-relaxed text-zinc-400">
            Every package is performed at your location, with everything we need on board.
            Tap any package to book it.
          </p>
          <button
            type="button"
            onClick={() => scrollToSection("#quiz")}
            data-testid="services-quiz-link"
            className="mt-4 inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:text-[#E60000]"
          >
            <HelpCircle size={14} className="text-[#E60000]" />
            Not sure? Take the 30-second quiz
          </button>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-12">
        {config.packages.map((pkg) => (
          <PackageCard key={pkg.id} pkg={pkg} />
        ))}
      </div>

      {/* Add-ons */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mt-16"
      >
        <h3 className="font-display mb-6 flex items-center gap-4 text-lg font-semibold uppercase tracking-[0.25em] text-white">
          Premium add-ons
          <span className="h-px flex-1 bg-white/10" />
        </h3>
        <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-5">
          {config.add_ons.map((a, i) => {
            const Icon = iconFor(a.icon);
            return (
              <div
                key={`${a.name}-${i}`}
                data-testid={`add-on-${a.icon}`}
                className="group bg-[#0A0A0A] p-6 text-left transition-[background-color] duration-500 hover:bg-[#101010]"
              >
                <Icon
                  size={24}
                  strokeWidth={1.75}
                  className="text-[#E60000] transition-transform duration-300 group-hover:-translate-y-1"
                />
                <div className="font-display mt-4 text-sm font-semibold uppercase leading-tight tracking-wide text-white">
                  {a.name}
                </div>
                <div className="font-display mt-1.5 text-xl font-bold text-[#E60000]">{a.price}</div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Trust note */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mt-10 flex items-start gap-4 border border-white/10 bg-white/[0.03] p-6"
      >
        <ShieldCheck size={26} className="mt-0.5 shrink-0 text-[#E60000]" />
        <p className="text-sm leading-relaxed text-zinc-300 md:text-base">
          <span className="font-semibold text-white">No surprise quotes — ever.</span> Final
          pricing depends on your vehicle's size and condition, and it's always confirmed with
          you <span className="text-[#E60000] font-semibold">before any work begins</span>.
          What we quote is what you pay.
        </p>
      </motion.div>
    </div>
  </section>
  );
};

export default Services;
